import type { AgeGroup, Avatar, ConsentMap, ConsentPurpose } from '@il-colpo/shared/account';
import type { Language } from '@il-colpo/shared/settings';
import type { MetaClient } from '../meta/types.ts';
import type { AdminClient, StaffRole } from '../admin/types.ts';
import type { SocialClient } from '../social/types.ts';

/**
 * What the app asks of an account service. Two things implement it for real (a Supabase project,
 * `supabaseAccount.ts`, and a stand-in for development and tests, `dev/`), and a third says there is
 * none (`noAccount.ts`): the game is complete without an account, which only adds a profile,
 * cross-save and, from milestone 6, playing online. Screens never see more than this.
 */

export type Provider = 'google' | 'discord' | 'apple';
export const PROVIDERS: readonly Provider[] = ['google', 'discord', 'apple'];

/**
 * The ways of getting in that a build offers besides the guest one (which is always there). A project
 * decides what really works: an e-mail with a six-digit code needs a mail service of its own (the
 * default of Supabase sends a link and no code, and two a day), a provider needs the keys of that
 * provider. A way that does not work leads nowhere ("this code is wrong"), so the build says which
 * ones are on.
 */
export interface AuthMethods {
  /** E-mail and password. The project must not ask to confirm the e-mail (no mail service, no code). */
  password: boolean;
  /** Everything that sends a six-digit code by e-mail: confirming an address, a one-time sign-in, a new password. */
  code: boolean;
  providers: readonly Provider[];
}

export const ALL_AUTH_METHODS: AuthMethods = { password: true, code: true, providers: PROVIDERS };

export interface AccountUser {
  id: string;
  email: string | null;
  /** A guest: a session with no e-mail and no provider, which can be turned into an account later. */
  anonymous: boolean;
  emailConfirmed: boolean;
  /** How the player has signed in so far: `email`, `google`, `discord`, `apple`. */
  providers: string[];
  /** Authenticator-app factors that are set up (the unconfirmed ones are not listed). */
  totpFactors: { id: string }[];
  /** The team role in the token (`app_metadata.role`, set in the service's dashboard), or none. */
  staffRole: StaffRole | null;
}

export interface AccountSnapshot {
  /** `loading` while a saved session is being restored. */
  status: 'loading' | 'signedOut' | 'signedIn';
  user: AccountUser | null;
  /** True when a second factor is set up and has not been given in this session. */
  needsSecondFactor: boolean;
}

export const SIGNED_OUT: AccountSnapshot = {
  status: 'signedOut',
  user: null,
  needsSecondFactor: false,
};

export type AuthErrorCode =
  | 'invalidCredentials'
  | 'emailNotConfirmed'
  | 'userExists'
  | 'weakPassword'
  | 'invalidEmail'
  | 'rateLimited'
  | 'captchaFailed'
  | 'codeInvalid'
  | 'secondFactorInvalid'
  | 'providerDisabled'
  | 'signupDisabled'
  | 'notSignedIn'
  | 'notConfigured'
  | 'network'
  // The data side:
  | 'conflict'
  | 'nicknameCooldown'
  | 'nicknameTaken'
  | 'ageLocked'
  | 'invalid'
  | 'tooLarge'
  | 'forbidden'
  | 'notFound'
  | 'unknown';

export interface AuthError {
  code: AuthErrorCode;
  /** What the service said, for the log: never shown to the player as it is. */
  message: string;
}

export type Result<T = void> = { ok: true; value: T } | { ok: false; error: AuthError };

export const ok = <T>(value: T): Result<T> => ({ ok: true, value });
export const okVoid: Result = { ok: true, value: undefined };
export const fail = (
  code: AuthErrorCode,
  message: string = code,
): { ok: false; error: AuthError } => ({
  ok: false,
  error: { code, message },
});

export interface Profile {
  id: string;
  nickname: string;
  tag: number;
  avatar: Avatar;
  language: Language;
  ageGroup: AgeGroup | null;
  nicknameChangedAt: string | null;
  createdAt: string;
}

export interface ProfilePatch {
  nickname?: string;
  avatar?: Avatar;
  language?: Language;
}

export interface TotpEnrollment {
  factorId: string;
  /** The secret to type into an authenticator app, and the `otpauth://` link that holds it. */
  secret: string;
  uri: string;
  /** The QR code as an image source (a `data:image/svg+xml` URL); empty if the service sent none. */
  qrImage: string;
}

export type CodePurpose = 'signIn' | 'signUp' | 'link' | 'recovery';

export type DocumentKind = 'settings' | 'progress';

export interface StoredDocument {
  data: unknown;
  version: number;
}

export interface AccountClient {
  readonly kind: 'supabase' | 'demo' | 'none';
  snapshot: () => AccountSnapshot;
  subscribe: (listener: (snapshot: AccountSnapshot) => void) => () => void;
  /** Restores a saved session and starts following the service. */
  start: () => Promise<void>;
  stop: () => void;

  /** What the player has earned by playing: XP, rap sheet, ratings, trophies, cosmetics, challenges. */
  meta: () => MetaClient;

  /** Friends, blocks, reports and notifications. */
  social: () => SocialClient;

  /** What the team does: reports, sanctions, flags, news. The service refuses it to anybody who is not staff. */
  admin: () => AdminClient;

  /** The access token of the signed-in player: what the game server is shown. Null when nobody is signed in. */
  accessToken: () => Promise<string | null>;

  // ---- Getting in -------------------------------------------------------------------------
  /** `language` names the guest's profile (Ospite or Guest); without it the service chooses Italian. */
  signInAsGuest: (options?: { language?: Language; captchaToken?: string }) => Promise<Result>;
  signUp: (
    email: string,
    password: string,
    options: { language: Language; captchaToken?: string },
  ) => Promise<Result<{ needsConfirmation: boolean }>>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<Result>;
  /** The passwordless way in: a one-time code by e-mail. */
  sendCode: (
    email: string,
    options: { language: Language; captchaToken?: string },
  ) => Promise<Result>;
  /**
   * The six-digit code of an e-mail: to sign in (`signIn`), to confirm a new account (`signUp`),
   * to confirm the e-mail of a guest who is becoming an account (`link`), or to come back to an
   * account whose password was forgotten (`recovery`).
   */
  verifyCode: (email: string, code: string, purpose?: CodePurpose) => Promise<Result>;
  /** Sends again the code that confirms a new account (`signUp`) or a new e-mail (`link`). */
  resendCode: (email: string, purpose: 'signUp' | 'link') => Promise<Result>;
  /** Leaves the page for the provider, and comes back signed in. */
  signInWithProvider: (provider: Provider) => Promise<Result>;
  /**
   * Finishes a sign-in that came back through a link of the native app (`ilcolpo://auth/callback?code=…`,
   * from the system browser or an e-mail). False: the link is not one the service can use.
   */
  handleAuthUrl: (url: string) => Promise<boolean>;
  sendPasswordReset: (email: string, captchaToken?: string) => Promise<Result>;
  setPassword: (password: string) => Promise<Result>;

  // ---- Turning a guest into an account ----------------------------------------------------
  linkEmail: (email: string, password: string) => Promise<Result<{ needsConfirmation: boolean }>>;
  linkProvider: (provider: Provider) => Promise<Result>;

  signOut: (everywhere: boolean) => Promise<Result>;

  // ---- Second factor ----------------------------------------------------------------------
  enrollTotp: () => Promise<Result<TotpEnrollment>>;
  confirmTotp: (factorId: string, code: string) => Promise<Result>;
  verifySecondFactor: (code: string) => Promise<Result>;
  removeTotp: (factorId: string) => Promise<Result>;

  // ---- The player's data ------------------------------------------------------------------
  profile: () => Promise<Result<Profile>>;
  updateProfile: (patch: ProfilePatch) => Promise<Result<Profile>>;
  declareAge: (group: AgeGroup) => Promise<Result>;
  consents: () => Promise<Result<ConsentMap>>;
  recordConsent: (purpose: ConsentPurpose, version: string, granted: boolean) => Promise<Result>;
  readDocument: (kind: DocumentKind) => Promise<Result<StoredDocument | null>>;
  /** `expectedVersion` is the version last read (null: there was none). A stale one is a `conflict`. */
  saveDocument: (
    kind: DocumentKind,
    data: unknown,
    expectedVersion: number | null,
  ) => Promise<Result<{ version: number }>>;

  // ---- The player's rights over it --------------------------------------------------------
  exportData: () => Promise<Result<unknown>>;
  deletion: () => Promise<Result<{ scheduledFor: string } | null>>;
  /** A grace of 0 days deletes the account at once, and signs out. */
  requestDeletion: (graceDays: number) => Promise<Result<{ scheduledFor: string | null }>>;
  cancelDeletion: () => Promise<Result<boolean>>;
}
